import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { accountByCode, netoDeVenta, type EstadoResultados, type Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import type { DineroDeAnalisis, Tramo } from '../lib/analisis.js';
import { asientosDelRango } from '../lib/libros.js';
import { useT } from '../i18n/index.js';
import { etiquetaCuenta } from '../i18n/dominio.js';
import { claveDeForma } from './FiltrosHistorial.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { DetalleDeCifra, useFechas, type Renglon } from './PiezasDeAnalisis.js';

/**
 * What the figures of «Análisis» add up, each in a sheet (the owner, 2026-10-13: «tap any figure to
 * see the records behind it»): the period's sales, the period's expenses as the books carry them,
 * one expense account's, and what was left.
 */

/**
 * The period's sales, the newest first: what «Vendido», «Utilidad bruta», «Ventas» and «Venta
 * promedio» add up, each sale as they count it (`netoDeVenta`): what came back taken off, delivery
 * and cash rounding aside. A voided sale is in none of them, and not here.
 */
export function VentasDelPeriodo({
  ventas,
  tramo,
  dinero,
  onCerrar,
}: {
  ventas: readonly Venta[];
  tramo: Tramo;
  dinero: DineroDeAnalisis;
  onCerrar: () => void;
}) {
  const t = useT();
  const f = useFechas();
  const nombres = useLiveQuery(async () => new Map((await db.clientes.toArray()).map((c) => [c.id, c.nombre])), [], null);
  const vigentes = ventas
    .filter((v) => !v.anulada && v.fecha >= tramo.desde && v.fecha <= tramo.hasta)
    .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.ticket.localeCompare(a.ticket, undefined, { numeric: true }));
  let ingreso = 0;
  let utilidad = 0;
  const netos = vigentes.map((v) => {
    const neto = netoDeVenta(v);
    ingreso += neto.ingreso;
    utilidad += neto.ingreso - neto.costo;
    return neto;
  });
  const renglones: Renglon[] | null =
    nombres === null
      ? null
      : vigentes.map((v, i) => {
          const neto = netos[i]!;
          const formas = [...new Set(v.pagos.filter((p) => p.estado !== 'rechazado').map((p) => t(claveDeForma(p.forma))))].join(', ');
          const quien = v.clienteId ? (nombres.get(v.clienteId) ?? '—') : t('clientes.mostrador');
          return {
            clave: v.id,
            titulo: `${t('analisis.detalle.ticket', { ticket: v.ticket })} · ${quien}`,
            detalle: [f.conDia(v.fecha), formas, t('analisis.detalle.dejo', { monto: dinero(neto.ingreso - neto.costo) })].filter(Boolean).join(' · '),
            valor: dinero(neto.ingreso),
            rojo: neto.ingreso - neto.costo < 0,
          };
        });
  return (
    <DetalleDeCifra
      titulo={t('analisis.detalle.ventas')}
      resumen={t('analisis.detalle.ventasResumen', { n: vigentes.length, monto: dinero(ingreso), utilidad: dinero(utilidad) })}
      renglones={renglones}
      vacio={t('analisis.sinVentas')}
      datos="ventas"
      onCerrar={onCerrar}
    />
  );
}

/**
 * The period's expenses as the books carry them, entry by entry: what «Gastos» adds up, or with
 * `cuenta` what one line of «En qué se va» does. Every expense account's movement, so the payroll,
 * a write-off or a drawer short is here as much as the rent; a correction or a void, the other way.
 */
export function GastosDelPeriodo({
  tramo,
  cuenta,
  titulo,
  dinero,
  onCerrar,
}: {
  tramo: Tramo;
  /** One expense account's code; absent, all of them. */
  cuenta?: string;
  titulo: string;
  dinero: DineroDeAnalisis;
  onCerrar: () => void;
}) {
  const t = useT();
  const f = useFechas();
  const asientos = useLiveQuery(() => asientosDelRango(tramo), [tramo.desde, tramo.hasta], null);
  let total = 0;
  const renglones: Renglon[] | null =
    asientos === null
      ? null
      : asientos
          .flatMap((e) =>
            e.lineas.flatMap((l, i) => {
              const acc = accountByCode(l.cuenta);
              if (!acc || acc.nature !== 'gasto' || (cuenta && l.cuenta !== cuenta)) return [];
              const monto = l.debe - l.haber;
              if (monto === 0) return [];
              total += monto;
              const nombre = etiquetaCuenta(acc.code, acc.name);
              return [
                {
                  fecha: e.fecha,
                  renglon: {
                    clave: `${e.eventId}:${i}`,
                    titulo: e.concepto || nombre,
                    detalle: cuenta ? f.conDia(e.fecha) : `${f.conDia(e.fecha)} · ${nombre}`,
                    valor: dinero(monto),
                    rojo: monto < 0,
                  },
                },
              ];
            }),
          )
          .sort((a, b) => b.fecha.localeCompare(a.fecha))
          .map((x) => x.renglon);
  return (
    <DetalleDeCifra
      titulo={titulo}
      resumen={renglones === null ? undefined : t('analisis.detalle.gastosResumen', { n: renglones.length, monto: dinero(total) })}
      renglones={renglones}
      vacio={t('analisis.sinGastos')}
      datos={cuenta ? `cuenta-${cuenta}` : 'gastos'}
      onCerrar={onCerrar}
    />
  );
}

/** What was left, as «Contabilidad» works it out: what came in, what it cost, the expenses. */
export function ResultadoDelPeriodo({
  resultado,
  tramo,
  dinero,
  onCerrar,
}: {
  resultado: EstadoResultados;
  /** The days it is of: «Ver en Contabilidad» opens on them, where it opened on this month whatever they were. */
  tramo: Tramo;
  dinero: DineroDeAnalisis;
  onCerrar: () => void;
}) {
  const t = useT();
  const fila = (etiqueta: string, monto: number, fuerte = false) => (
    <div className={`flex justify-between gap-3 py-1.5 ${fuerte ? 'border-t border-slate-800 font-semibold' : 'text-slate-300'}`}>
      <span>{etiqueta}</span>
      <span className={`tabular ${monto < 0 ? 'text-rose-300' : ''}`}>{dinero(monto)}</span>
    </div>
  );
  return (
    <Modal
      titulo={t('analisis.utilidadNeta')}
      onCerrar={onCerrar}
      pie={
        <Link to="/contabilidad" search={{ desde: tramo.desde, hasta: tramo.hasta }} className="btn-secundario w-full" data-detalle-contabilidad>
          <Ico icono="ganancia" />
          {t('analisis.detalle.verContabilidad')}
        </Link>
      }
    >
      <div className="text-sm" data-detalle-cifra="resultado">
        {fila(t('conta.ingresos'), resultado.totalIngresos)}
        {fila(t('conta.costoVentas'), -resultado.totalCostos)}
        {fila(t('conta.utilidadBruta'), resultado.utilidadBruta, true)}
        {fila(t('conta.gastos'), -resultado.totalGastos)}
        {fila(t('conta.utilidadNeta'), resultado.utilidadNeta, true)}
      </div>
    </Modal>
  );
}
